import React from 'react';
import PropTypes from 'prop-types';
import * as types from '../../../constants/actionTypes';
import Chart from 'react-apexcharts';
import { TASK_COMPLETION_SLICES, HEATMAP_VIEWS, DATE_FORMAT, FONT_FAMILY } from '../../../constants/constants';
import Notification from '../../Common/Notification';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import { Dropdown } from 'primereact/dropdown';
import ResourceAllocationTasksDrawer from './ResourceAllocationTasksDrawer';
import Button from '../../Common/Button';
import './ResourceAllocationView.scss';
import Moment from 'moment';
import ExportButton from './ExportButton/ExportButton';

export class ResourceAllocationView extends React.Component {
	constructor(props, context) {
		super(props, context);

		this.state = {
			showTasksDrawer: false,
			isLoading: true,

			// if projects enabled it should be the default selection
			selectedDivider: this.props.settings.isProjectWidgetActive ? TASK_COMPLETION_SLICES.Project : TASK_COMPLETION_SLICES.Discipline,
			selectedView: 'month',
			pageNumber: 1,

			// things returned from api to remember
			startDateRange: Moment(),
			endDateRange: Moment(),
			isLastPage: true,
			series: null,
			tasks: [],
			height: 100,
			containerHeight: 500,
			options: {
				stroke: {
					width: 0,
				},
				yaxis: {
					minHeight: 30,
					maxHeight: 30,
					reversed: true,
					labels: {
						style: {
							fontFamily: FONT_FAMILY,
						},
					},
					axisBorder: {
						show: true,
						color: '#8b9095',
					},
					axisTicks: {
						show: false,
					},
				},
				xaxis: {
					minHeight: 30,
					maxHeight: 30,
					labels: {
						style: {
							fontFamily: FONT_FAMILY,
						},
					},
					axisBorder: {
						show: true,
						color: '#8b9095',
					},
					axisTicks: {
						show: false,
					},
				},
				grid: {
					show: false,
				},
				chart: {
					events: {
						click: this.heatmap_click,
					},
					toolbar: {
						show: false,
					},
					animations: {
						enabled: true,
						speed: 250,
						animateGradually: {
							enabled: true,
							delay: 100,
						},
						dynamicAnimation: {
							enabled: true,
							speed: 100,
						},
					}
				},
				plotOptions: {
					heatmap: {
						distributed: true,
						stroke: {
							width: 1,
						},
					},
				},
				colors: ['#00a4fb'],
				dataLabels: {
					style: {
						colors: ['#666'],
						fontFamily: FONT_FAMILY,
					},
					formatter: function (val) {
						return val == 0 ? '' : val;
					},
				},
			},

			// click tracking
			lastClickedSeriesName: '',
			lastClickedDateStart: '',
			lastClickedDateEnd: '',
			exporting: {
				enabled: false
			}
		};
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.props.locations && this.props.locations.length !== prevProps.locations.length) {
			this.search(1);
		} else if (this.props.lastSearchTime && this.props.lastSearchTime !== prevProps.lastSearchTime) {
			this.search(1);
		} else {
			for (let i = 0; i < this.props.locations.length; i++) {
				if (!prevProps.locations.find((location) => location.id === this.props.locations[i].id)) {
					this.search(1);
					break;
				}
			}
		}

		if (prevState.containerHeight !== this.state.containerHeight) {
			this.updateHeight();
		}
	}

	search = async (pageNumber) => {
		try {
			// since we don't know the start of the first task or the end, we have to depend on the API for that logic
			// the api is smart enough to figure out the date range based on the pageNumber and selectedView
			const locationKeys = [];
			this.props.locations.forEach((location) => locationKeys.push(location.id));

			let searchParams = {
				...this.props.searchParams,
				locationKeys: locationKeys,
				divider: this.state.selectedDivider,
				view: this.state.selectedView,
				pageNumber: pageNumber,
			};

			const response = await this.props.locationTaskActions.getResourceAllocations(searchParams);
			if (response.type === types.LOAD_TASK_RESOURCE_ALLOCATIONS_SUCCESS) {
				this.setState({
					series: response.payload.seriesData,
					isLoading: false,
					pageNumber: pageNumber,
					startDateRange: response.payload.startDateRange,
					endDateRange: response.payload.endDateRange,
					isLastPage: response.payload.isLastPage,
				});
			} else if (response.type === types.LOAD_TASK_RESOURCE_ALLOCATIONS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	getTasks = async (seriesName, startDate, endDate) => {
		try {
			const locationKeys = [];
			this.props.locations.forEach((location) => locationKeys.push(location.id));

			let searchParams = {
				...this.props.searchParams,
				locationKeys: locationKeys,
				divider: this.state.selectedDivider,
				view: this.state.selectedView,
				seriesName: seriesName,
				targetDateStart: startDate,
				targetDateEnd: endDate,
			};

			const response = await this.props.locationTaskActions.getResourceAllocationsDetails(searchParams);
			if (response.type === types.LOAD_TASK_RESOURCE_ALLOCATIONS_DETAILS_SUCCESS) {
				this.setState({
					tasks: response.payload.tasks,
					showTasksDrawer: true,
					lastClickedSeriesName: seriesName,
					lastClickedDateStart: startDate.format(DATE_FORMAT),
					lastClickedDateEnd: endDate.format(DATE_FORMAT),
				});
			} else if (response.type === types.LOAD_TASK_RESOURCE_ALLOCATIONS_DETAILS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	onDropDownChange = (e) => {
		this.setState({ ['selectedDivider']: e.value, isLoading: true, currentStartDate: null }, () => this.search(1));
	};

	onChangeView = (view) => {
		this.setState({ selectedView: view, isLoading: true, currentStartDate: null }, () => this.search(1));
	};

	updateHeight = () => {
		const newHeight = this.getHeight();
		if (!isNaN(newHeight)) {
			this.setState({ containerHeight: newHeight });
		}
	};

	getHeight = () => {
		const seriesCount = Array.isArray(this.state.series) ? this.state.series.length : 0;
		const calculatedHeight = 100 + seriesCount * 30;
		return isNaN(calculatedHeight) ? 100 : calculatedHeight;
	};

	onNavPrev = () => {
		if (this.state.pageNumber > 1 && this.state.selectedView !== HEATMAP_VIEWS.Month.value) {
			this.setState({ isLoading: true }, () => this.search(this.state.pageNumber - 1));
		}
	};

	onNavNext = () => {
		if (this.state.selectedView !== HEATMAP_VIEWS.Month.value) {
			this.setState({ isLoading: true }, () => this.search(this.state.pageNumber + 1));
		}
	};

	getRangeDisplay = () => {
		return Moment(this.state.startDateRange).add(1, 'day').format(DATE_FORMAT) + ' - ' + Moment(this.state.endDateRange).format(DATE_FORMAT);
	};

	heatmap_click = (event, chartContext, config) => {
		// figure out date range, make api call to get task info, open drawer
		if (config.seriesIndex != -1 && config.dataPointIndex != -1) {
			let x = this.state.series[config.seriesIndex].data[config.dataPointIndex].x;
			let seriesName = this.state.series[config.seriesIndex].name;
			// build a date range for the clicked square based on selected view
			let start;
			let end;
			switch (this.state.selectedView) {
				case HEATMAP_VIEWS.Month.value:
					// MM YYYY
					start = Moment('1-' + x, 'DD-MMM YYYY');
					end = Moment(start).add(1, 'month');
					break;
				case HEATMAP_VIEWS.Week.value:
					start = Moment(x, 'MMM DD YYYY');
					end = Moment(start).add(1, 'week');
					break;
				case HEATMAP_VIEWS.Day.value:
					start = Moment(x, 'MMM DD YYYY');
					end = Moment(start).add(1, 'day');
					break;
			}
			this.getTasks(seriesName, start, end);
		}
	};

	onCloseTasksDrawer = () => {
		this.setState({ showTasksDrawer: false });
	};

	handleExport = async (format) => {
		if (this.chartRef) {
			try {
				if (format === 'png' || format === 'svg') {
					const { imgURI } = await this.chartRef.chart.dataURI();

					// Create a download link for PNG or SVG
					const link = document.createElement('a');
					if (format === 'png') {
						link.href = imgURI; // Use the PNG dataURI
						link.download = 'chart.png'; // Set the file name for PNG
						link.click(); // Trigger the download
					} else if (format === 'svg') {

						const chart = this.chartRef.chart;
						if (chart) {
							// Get the SVG element from the chart DOM
							const svgElement = chart.el.querySelector('svg');
							if (svgElement) {
								const svgData = new XMLSerializer().serializeToString(svgElement);
								const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
								const url = URL.createObjectURL(svgBlob);
								const link = document.createElement('a');
								link.href = url;
								link.download = 'chart.svg'; // Set the file name for SVG
								document.body.appendChild(link);
								link.click(); // Trigger the download
								document.body.removeChild(link); // Clean up
								URL.revokeObjectURL(url); // Free up memory
							} else {
								console.error('SVG element not found.');
							}
						} else {
							console.error('Chart reference is invalid.');
						}
					}

				} else if (format === 'csv') {
					// Extract the chart data
					const seriesData = this.state.series;
					const csvContent = [];

					// Create a header row with series names and x-axis label
					const headerRow = ['category', ...seriesData.map(series => series.name)];
					csvContent.push(headerRow.join(','));

					// Create a map to store y-values for each x value
					const xValues = {};

					seriesData.forEach((series) => {
						series.data.forEach((point) => {
							if (!xValues[point.x]) {
								xValues[point.x] = {}; // Initialize if not already present
							}
							xValues[point.x][series.name] = point.y; // Store the y value under the series name
						});
					});

					// Populate the CSV rows
					Object.keys(xValues).forEach((xValue) => {
						const row = [xValue];
						seriesData.forEach((series) => {
							row.push(xValues[xValue][series.name] !== undefined ? xValues[xValue][series.name] : ''); // Add y value or empty string
						});
						csvContent.push(row.join(','));
					});

					// Convert array to CSV format
					const csvString = `data:text/csv;charset=utf-8,${csvContent.join('\n')}`;
					const link = document.createElement('a');
					link.href = encodeURI(csvString);
					link.download = 'chart.csv'; // Set the file name for CSV
					link.click(); // Trigger the download
				}
			} catch (error) {
				console.error('Export error:', error);
				Notification('error', 'Export error', error.message);
			}
		}
	};


	render() {
		const originalOptions = [
			{ id: TASK_COMPLETION_SLICES.Discipline, name: TASK_COMPLETION_SLICES.Discipline },
			{ id: TASK_COMPLETION_SLICES.Department, name: TASK_COMPLETION_SLICES.Department },
			{ id: TASK_COMPLETION_SLICES.Responsible, name: TASK_COMPLETION_SLICES.Responsible },
			{ id: TASK_COMPLETION_SLICES.Community, name: TASK_COMPLETION_SLICES.Community },
		];

		if (this.props.settings.isProjectWidgetActive) {
			originalOptions.push({ id: TASK_COMPLETION_SLICES.Project, name: TASK_COMPLETION_SLICES.Project });
		}

		const dividedOptions = originalOptions.map((option) => ({
			label: option.name,
			value: option.id,
		}));

		return (
			<div>
				<LoadingIndicator isLoading={this.state.isLoading}>
					<div
						className='workload-div'
						style={{
							overflow: 'hidden',
							background: '#f8f9fa',
							marginTop: '20px',
							paddngLeft: '7px',
						}}>
						<div className='header-top-buttons'>
							<div className='nav-controls'>
								<Dropdown
									className='ui-column-filter'
									value={this.state.selectedDivider}
									options={dividedOptions}
									onChange={this.onDropDownChange}
									style={{
										width: '250px'
									}}
								/>
								{this.state.selectedView !== HEATMAP_VIEWS.Month.value && this.state.pageNumber !== 1 && (
									<Button type='button' iconName='chevron-left' bootstrapClass='light button-prev' onClick={this.onNavPrev} />
								)}
								{this.state.selectedView !== HEATMAP_VIEWS.Month.value && !this.state.isLastPage && (
									<Button type='button' iconName='chevron-right' bootstrapClass='light button-next' onClick={this.onNavNext} />
								)}
							</div>

							<div>
								<h2>{this.getRangeDisplay()}</h2>
							</div>
							<div>
								<div className='float-end'>
									<div className='btn-group'>
										<button
											type='button'
											className={this.state.selectedView === HEATMAP_VIEWS.Month.value ? 'btn selected-button' : 'btn btn-light'}
											onClick={() => this.onChangeView(HEATMAP_VIEWS.Month.value)}>
											{HEATMAP_VIEWS.Month.label}
										</button>
										<button
											type='button'
											className={this.state.selectedView === HEATMAP_VIEWS.Week.value ? 'btn selected-button' : 'btn btn-light'}
											onClick={() => this.onChangeView(HEATMAP_VIEWS.Week.value)}>
											{HEATMAP_VIEWS.Week.label}
										</button>
										<button
											type='button'
											className={this.state.selectedView === HEATMAP_VIEWS.Day.value ? 'btn selected-button' : 'btn btn-light'}
											onClick={() => this.onChangeView(HEATMAP_VIEWS.Day.value)}>
											{HEATMAP_VIEWS.Day.label}
										</button>
									</div>

									<ExportButton onExport={this.handleExport}></ExportButton>
								</div>


							</div>
						</div>
						<div className='chart-section' style={{ height: this.getHeight(), background: 'white' }}>
							{!this.state.isLoading && this.state.series && (
								<Chart
									ref={(chart) => (this.chartRef = chart)}
									type='heatmap'
									options={this.state.options}
									series={this.state.series}
									width='100%'
									height='100%'
									style={{ width: '100%', height: this.getHeight() }}
								/>
							)}
							{!this.state.series && <div>No Results Found</div>}
						</div>
					</div>
				</LoadingIndicator>
				{this.state.showTasksDrawer && (
					<ResourceAllocationTasksDrawer
						tasks={this.state.tasks}
						seriesName={this.state.lastClickedSeriesName}
						dateRangeStart={this.state.lastClickedDateStart}
						dateRangeEnd={this.state.lastClickedDateEnd}
						onCancel={this.onCloseTasksDrawer}
						showTask={this.props.showTask}
					/>
				)}
			</div>
		);
	}
}

ResourceAllocationView.propTypes = {
	settings: PropTypes.object.isRequired,
	locationTaskActions: PropTypes.object.isRequired,
	locations: PropTypes.array.isRequired,
	searchParams: PropTypes.object.isRequired,
	lastSearchTime: PropTypes.instanceOf(Date),
	showTask: PropTypes.func.isRequired,
};

export default ResourceAllocationView;
